<template v-once>
  <vs-card v-if="job" :to="linkTo">
    <div>
      <v-row align="center">
        <v-col>
          <vs-text> {{ jobTitle }}</vs-text>
        </v-col>
        <v-col class="shrink">
          <v-icon small :color="priority?.color">
            {{ priority?.icon }}
          </v-icon>
        </v-col>
        <v-col class="shrink">
          <job-status-chip :status="finalStatus" />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <vs-heading>{{ job.activity_name }}</vs-heading>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <vs-text type="caption">{{ taskText }}</vs-text>
        </v-col>
      </v-row>
    </div>

    <v-row align="center">
      <v-col class="shrink pr-4">
        <v-icon color="grey darken-2">calendar_today</v-icon>
      </v-col>

      <v-col>
        <vs-text type="overline">Schedule</vs-text>
        <vs-text>{{ scheduleTime }}</vs-text>
      </v-col>
    </v-row>
    <job-card-locations-detail
      :inventory="inventory"
      :destination-location-name="job.destination_name"
    />
  </vs-card>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import JobStatusChip from '@/components/tickets/JobStatusChip.vue'
import { NO_APPROVAL } from '@/models/job'
import JobCardLocationsDetail from '../jobs/JobCardLocationsDetail.vue'
import { formatTimeRange } from '@/utils/date-formatting'
import { JobJson } from '@/models/types/job-json'
import moment from 'moment'
import { priorities } from '@/utils/constants'
import type { RouterLinkProps } from 'vue-router/types/router'
import { useStore } from 'vuex-composition-helpers'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'

type RouterLinkTo = RouterLinkProps['to']

const props = defineProps<{
  job: JobJson
  linkTo: RouterLinkTo
}>()

const store = useStore()

const company = computed(() => {
  return store.getters['companies/getByChildHubId'](props.job?.hub_id)
})
const jobTitle = computed(() => `Job #${props.job?.number} · ${company.value?.shortCode}`)

const priority = computed(() => priorities.find((p) => p.name === props.job.priority))

const scheduleTime = computed(() => {
  const startTime = props.job.requested_start && moment.utc(props.job.requested_start).local()
  const endTime = props.job.requested_end && moment.utc(props.job.requested_end).local()
  return formatTimeRange(startTime, endTime)
})

const finalStatus = computed(() =>
  props.job.approval_status !== null && props.job.approval_status !== NO_APPROVAL
    ? props.job.approval_status
    : props.job.status.trim(),
)

const { getInventoryTypeById } = useInventoryTypes()

const inventory = computed(() => {
  return props.job.inventory_requirements.map((inv) => ({
    name: getInventoryTypeById(inv.inventory_type_id)?.name,
    inventoryTypeId: inv.inventory_type_id,
    inventorySubTypeName: inv.name,
    inventorySubTypeId: inv.inventory_sub_type_id,
    quantity: inv.quantity,
    pickupLocationId: inv.pickup_location_id,
    pickupLocationName: inv.pickup_location_name,
    inventoryId: 0, // Not used
    position: inv.position,
    assetConditions: [], // Not used
  }))
})

const taskText = computed(() => {
  const assignment = props.job.assigned_resource_name || 'Unassigned'
  return `${props.job.resource_type_name} · ${assignment}`
})
</script>
